import React, { useEffect, useState, createContext, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// Conditional import based on environment
import MyMap from "../mymap/mymap";
import MyMapMock from "../mymap/MyMapMock";
import Navbar from "./navbar";
import './layout.css'
import TripPlanTree from "../tripPlanTree/TripPlanTree";
import EventLongDescViewer from "../../components/EventLongDescViewer";


export const locationContext = createContext();

const Layout = () => {
  const [location,setLocation] = useState({
    name : "",
    lat: 0,
    lng: 0,
    zoom: 1, // Default zoom level
  });
  const [searchMapLoc,setSearchMapLoc] = useState("Search...");
  const [user, setUser] = useState(null);
  const tripParamsFromNavigate= useLocation();
  const [leftWidth, setLeftWidth] = useState(window.innerWidth * 0.3); // Initial width: 30% of screen
  const navigate = useNavigate();

  // State for the addNode callback
  const [addNodeCallback, setAddNodeCallback] = useState();

  const memoizedSetAddNodeCallback = useCallback((callback) => {
    setAddNodeCallback((name,lat,lng,zoom, rating,user_ratings_total,url, formatted_address,formatted_phone_number,place_id) => callback(name,lat,lng,zoom, rating,user_ratings_total,url, formatted_address,formatted_phone_number,place_id));
  }, []);

  // --- Map search callback wiring ---
  const [searchMapCallback, setSearchMapCallback] = useState(null);
  // This function will be passed to MyMap, which will call it with its search function
  const registerSearchMapCallback = React.useCallback((fn) => {
    setSearchMapCallback(() => fn);
  }, []);

  const [clearTempMarker, setClearTempMarker] = useState(() => () => {});

  // Use useRef instead of useState for more reliable callback storage
  const clearSelectedPlaceRef = useRef(() => {});
  const setClearSelectedPlace = useCallback((fn) => {
    clearSelectedPlaceRef.current = fn;
  }, []);

  

  // Multi-selection state - lifted from TripPlanTree to share with MyMap
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState(new Set()); // Set of event IDs

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${BACKEND_URL}/auth-check`, {  method: 'GET',credentials: 'include' })
      .then((response) => response.json())
      .then((data) => {
        if (data) 
          {
            setUser(data)
          }
        else{
          navigate('/'); // Redirect to login if not authenticated
        }}).catch((error) => console.error('Error checking authentication:', error));
  }, [navigate]);

  const handleDrag = (e) => {
    const newWidth = e.clientX;
    const minWidth = window.innerWidth * 0.3; // 30% of screen
    setLeftWidth(Math.max(newWidth, minWidth));
  };

  // Responsive layout: mobile vs PC
  const isMobile = window.innerWidth <= 600;

  // --- Trip and marker logic lifted from TripPlanTree ---
const [trip, setTrip] = useState(null); // null until loaded
const [loading, setLoading] = useState(true);
// Persistent selection state for days, events, and eventID
const [selectedDayIdx, setSelectedDayIdx] = useState(null);
const [selectedEventIdx, setSelectedEventIdx] = useState(null);
const [selectedEventID, setSelectedEventID] = useState(null);
const [newEvents, setNewEvents] = useState({});
const [editingEvent, setEditingEvent] = useState(null);

// Fetch trip on mount (replace with your tripId logic as needed)
useEffect(() => {
  console.log('Loading trip from backend...');
  const tripId = tripParamsFromNavigate.state?.trip?._id || tripParamsFromNavigate.state?.trip?.id;
  if (!tripId) return;
  setLoading(true);
  fetch(`${BACKEND_URL}/newtrips/${tripId}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then(res => {
      if (!res.ok) throw new Error('Failed to fetch trip');
      return res.json();
    })
    .then(data => {
      setTrip(data);
      setLoading(false);
    })
    .catch(err => {
      console.error('Error loading trip from backend:', err);
      setLoading(false);
    });
}, [tripParamsFromNavigate]);

// Compute tripLocations for all events with valid lat/lng AND route events
const tripLocations = React.useMemo(() => {
  console.log('Computing trip locations...');
  if (!trip || !trip.days) return [];
  const locs = [];
  trip.days.forEach((day, dayIdx) => {
    day.events.forEach((event, eventIdx) => {
      // Handle regular location events (have lat and lng)
      if (
        event.location &&
        typeof event.location.lat === 'number' &&
        typeof event.location.lng === 'number' &&
        !isNaN(event.location.lat) &&
        !isNaN(event.location.lng)
      ) {
        locs.push({
          lat: event.location.lat,
          lng: event.location.lng,
          name: event.location.name,
          eventType: event.eventType || 'Other',
          zoom : event.location.zoom || 15, // Default zoom if not present
          formatted_phone_number: event.location.formatted_phone_number || null,
          url: event.location.url || '',
          rating: event.location.rating || null,  // Add rating if available
          user_ratings_total: event.location.user_ratings_total || null, // Add user ratings total if available
          formatted_address: event.location.formatted_address || '',  // Add formatted address if available
          weekday_text: event.location.weekday_text || [], // Add weekday text if available
          place_id: event.location.place_id || null, // Add place_id if available
          dayIdx,
          eventIdx,
          eventID: event._id || event.eventID || null
        });
      }
      // Handle route events (location exists but has no lat/lng, only zoom and _id)
      else if (event.location && 
               (typeof event.location.lat !== 'number' || typeof event.location.lng !== 'number' || 
                isNaN(event.location.lat) || isNaN(event.location.lng)) &&
               event.route && event.route.waypoints && event.route.waypoints.length > 0) {
        // For route events, we'll add a special marker representing the route
        // Use the first waypoint as the representative location, but mark it as a route
        const firstWaypoint = event.route.waypoints[0];
        if (firstWaypoint && typeof firstWaypoint.lat === 'number' && typeof firstWaypoint.lng === 'number') {
          locs.push({
            lat: firstWaypoint.lat,
            lng: firstWaypoint.lng,
            name: event.eventName || 'Route',
            eventType: 'Route', // Special event type for routes
            zoom: 12, // Zoom out more for routes
            formatted_phone_number: null,
            url: '',
            rating: null,
            user_ratings_total: null,
            formatted_address: `Route: ${event.route.startLocation || 'Start'} → ${event.route.endLocation || 'End'}`,
            weekday_text: [],
            place_id: null,
            dayIdx,
            eventIdx,
            eventID: event._id || event.eventID || null,
            isRoute: true, // Special flag to identify route events
            route: event.route // Include route data for special handling
          });
        }
      }
    });
  });
  return locs;
}, [trip]);

// Handler for adding a marker from the map
const handleAddMarker = React.useCallback((marker) => {
  if (selectedDayIdx != null && trip && trip.days && trip.days[selectedDayIdx]) {
    // Prefill new event for selected day
    setNewEvents(prev => ({
      ...prev,
      [selectedDayIdx]: {
        ...(prev[selectedDayIdx] || { eventType: 'Location', description: '' }),
        location: {
          name: marker.name,
          lat: marker.lat,
          lng: marker.lng,
          formatted_address: marker.formatted_address,
          place_id: marker.place_id,
          formatted_phone_number: marker.formatted_phone_number,
          url: marker.url,
          rating: marker.rating,
          user_ratings_total: marker.user_ratings_total,
          weekday_text: marker.weekday_text,
        }
      }
    }));
  }
}, [selectedDayIdx, trip]);


  // When an event is selected, update selectedEventID
useEffect(() => {
  if (
    selectedDayIdx != null &&
    selectedEventIdx != null &&
    trip &&
    trip.days &&
    trip.days[selectedDayIdx] &&
    trip.days[selectedDayIdx].events &&
    trip.days[selectedDayIdx].events[selectedEventIdx]
  ) {
    setSelectedEventID(trip.days[selectedDayIdx].events[selectedEventIdx]._id || null);
  }
}, [selectedDayIdx, selectedEventIdx, trip]);

  // --- Vertical splitter logic for right pane ---
  const [mapHeightRatio, setMapHeightRatio] = useState(0.7); // 70% map, 30% desc
  const splitterRef = useRef(null);
  
  // --- Mobile vertical splitter logic ---
  const [mobileTreeHeightRatio, setMobileTreeHeightRatio] = useState(0.45); // 45% tree, 55% map
  const mobileSplitterRef = useRef(null);
  const draggingRef = useRef(false);
  const mobileDraggingRef = useRef(false);

  // Find the currently selected event's longDescription
  let selectedLongDesc = '';
  if (
    trip &&
    selectedDayIdx != null &&
    selectedEventIdx != null &&
    trip.days &&
    trip.days[selectedDayIdx] &&
    trip.days[selectedDayIdx].events &&
    trip.days[selectedDayIdx].events[selectedEventIdx]
  ) {
    selectedLongDesc = trip.days[selectedDayIdx].events[selectedEventIdx].longDescription || '';
  }

  // Splitter mouse handlers
  const handleSplitterMouseDown = (e) => {
    draggingRef.current = true;
    document.body.style.cursor = 'row-resize';
    const startY = e.clientY;
    const pane = document.querySelector('.pane.right-pane');
    const paneRect = pane.getBoundingClientRect();
    const paneHeight = paneRect.height;
    const mapDiv = document.querySelector('.right-pane-map');
    const mapDivRect = mapDiv.getBoundingClientRect();
    const startMapHeight = mapDivRect.height;

    function onMouseMove(ev) {
      if (!draggingRef.current) return;
      const deltaY = ev.clientY - startY;
      let newMapHeight = startMapHeight + deltaY;
      // Clamp: minimum 70% of pane height, maximum 99%
      const minRatio = 0.7;
      const maxRatio = 0.99;
      let newRatio = newMapHeight / paneHeight;
      newRatio = Math.max(minRatio, Math.min(newRatio, maxRatio));
      setMapHeightRatio(newRatio);
    }
    function onMouseUp() {
      draggingRef.current = false;
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  };

  // Mobile splitter mouse handlers
  const handleMobileSplitterMouseDown = (e) => {
    e.preventDefault();
    mobileDraggingRef.current = true;
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    
    const container = document.querySelector('.mobile-vertical-layout');
    const containerRect = container.getBoundingClientRect();
    const containerHeight = containerRect.height;
    const containerTop = containerRect.top;

    function onMouseMove(ev) {
      if (!mobileDraggingRef.current) return;
      ev.preventDefault();
      
      const clientY = ev.type && ev.type.includes('touch') ? ev.touches[0].clientY : ev.clientY;
      const relativeY = clientY - containerTop;
      
      // Calculate new ratio based on position within container
      let newRatio = relativeY / containerHeight;
      
      // Clamp: minimum 25% of container height, maximum 75%
      const minRatio = 0.25;
      const maxRatio = 0.75;
      newRatio = Math.max(minRatio, Math.min(newRatio, maxRatio));
      
      console.log('Mobile splitter drag:', { 
        clientY, 
        containerTop,
        relativeY, 
        containerHeight, 
        newRatio,
        oldRatio: mobileTreeHeightRatio 
      });
      
      setMobileTreeHeightRatio(newRatio);
    }
    
    function onMouseUp(ev) {
      ev.preventDefault();
      mobileDraggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onMouseMove);
      window.removeEventListener('touchend', onMouseUp);
      console.log('Mobile splitter drag ended');
    }
    
    // Add both mouse and touch event listeners
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onMouseMove);
    window.addEventListener('touchend', onMouseUp);
    console.log('Mobile splitter drag started');
  };

  // Choose which map component to use based on env
  const isTestMode = process.env.REACT_APP_DEV_MODE === 'test';
  const MapComponent = isTestMode ? MyMapMock : MyMap;

  return (
    <locationContext.Provider 
      value={{ location, setLocation ,searchMapLoc,setSearchMapLoc,trip,setTrip, addNodeCallback, setAddNodeCallback : memoizedSetAddNodeCallback }}>
      <div className="app-container">
        {/* Always render Navbar at the top, fixed position */}
        <Navbar trip={trip}   />
        {isMobile ? (
          <div className="mobile-vertical-layout">
            {/* Mobile Tree Section */}
            <div 
              className="mobile-tripplantree-wrapper"
              style={{
                height: `calc(${Math.round(mobileTreeHeightRatio * 100)}% - 6px)`,
                flex: 'none'
              }}
            >
              <TripPlanTree
                setAddNodeCallback={memoizedSetAddNodeCallback}
                setSearchMapCallback={setSearchMapCallback}
                searchMapCallback={searchMapCallback}
                onClearSelectedPlace={() => clearSelectedPlaceRef.current()}
                trip={trip}
                setTrip={setTrip}
                loading={loading}
                setLoading={setLoading}
                selectedDayIdx={selectedDayIdx}
                setSelectedDayIdx={setSelectedDayIdx}
                selectedEventIdx={selectedEventIdx}
                setSelectedEventIdx={setSelectedEventIdx}
                selectedEventID={selectedEventID}
                setSelectedEventID={setSelectedEventID}
                newEvents={newEvents}
                setNewEvents={setNewEvents}
                editingEvent={editingEvent}
                setEditingEvent={setEditingEvent}
                isMultiSelectMode={isMultiSelectMode}
                setIsMultiSelectMode={setIsMultiSelectMode}
                selectedEvents={selectedEvents}
                setSelectedEvents={setSelectedEvents}
              />
            </div>
            {/* Mobile Vertical Splitter */}
            <div
              ref={mobileSplitterRef}
              className="mobile-vertical-splitter"
              style={{
                height: 10,
                background: '#1557b0',
                cursor: 'row-resize',
                width: '100%',
                zIndex: 1000,
                userSelect: 'none',
                touchAction: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseDown={handleMobileSplitterMouseDown}
              onTouchStart={(e) => {
                e.preventDefault();
                const touch = e.touches[0];
                const mouseEvent = {
                  clientY: touch.clientY,
                  preventDefault: () => e.preventDefault()
                };
                handleMobileSplitterMouseDown(mouseEvent);
              }}
            >
              <div style={{
                width: 40,
                height: 4,
                background: 'rgba(255,255,255,0.3)',
                borderRadius: 2
              }}></div>
            </div>
            {/* Mobile Map Section */}
            <div 
              className="mobile-mymap-wrapper"
              style={{
                height: `calc(${Math.round((1 - mobileTreeHeightRatio) * 100)+30}%)`,
                flex: 'none'
              }}
            >
              {/* Use MapComponent instead of MyMap */}
              <MapComponent
                addNodeCallback={addNodeCallback}
                registerSearchMapCallback={registerSearchMapCallback}
                tripLocations={tripLocations}
                onAddMarker={handleAddMarker}
                setClearTempMarkerCallback={setClearTempMarker}
                setClearSelectedPlaceCallback={setClearSelectedPlace}
                selectedDayIdx={selectedDayIdx}
                selectedEventIdx={selectedEventIdx}
                trip={trip}
                setSelectedDayIdx={setSelectedDayIdx}
                setSelectedEventIdx={setSelectedEventIdx}
                selectedEventID={selectedEventID}
                setSelectedEventID={setSelectedEventID}
                editingEvent={editingEvent}
                setEditingEvent={setEditingEvent}
                isMultiSelectMode={isMultiSelectMode}
                selectedEvents={selectedEvents}
              />
            </div>
          </div>
        ) : (
          <div className="my-layout" >
            <div className="pane left-pane" style={{ width: leftWidth }}>
              <TripPlanTree
                setAddNodeCallback={memoizedSetAddNodeCallback}
                setSearchMapCallback={setSearchMapCallback}
                searchMapCallback={searchMapCallback}
                onRemoveMarkerEvent={clearTempMarker}
                onClearSelectedPlace={() => clearSelectedPlaceRef.current()}
                trip={trip}
                setTrip={setTrip}
                loading={loading}
                setLoading={setLoading}
                selectedDayIdx={selectedDayIdx}
                setSelectedDayIdx={setSelectedDayIdx}
                selectedEventIdx={selectedEventIdx}
                setSelectedEventIdx={setSelectedEventIdx}
                selectedEventID={selectedEventID}
                setSelectedEventID={setSelectedEventID}
                newEvents={newEvents}
                setNewEvents={setNewEvents}
                editingEvent={editingEvent}
                setEditingEvent={setEditingEvent}
                isMultiSelectMode={isMultiSelectMode}
                setIsMultiSelectMode={setIsMultiSelectMode}
                selectedEvents={selectedEvents}
                setSelectedEvents={setSelectedEvents}
              />
            </div>
            <div
              className="splitter"
              onMouseDown={(e) => {
                document.addEventListener("mousemove", handleDrag);
                document.addEventListener("mouseup", () => {
                  document.removeEventListener("mousemove", handleDrag);
                });
              }}
            ></div>
            {/* --- Right pane with vertical stack --- */}
            <div className="pane right-pane" style={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
              {/* Map area */}
              <div
                className="right-pane-map"
                style={{
                  flex: `0 1 ${Math.round(mapHeightRatio * 100)}%`,
                  minHeight: 100,
                  maxHeight: '70%',
                  height: 'auto', // Remove any fixed height
                  overflow: 'hidden'
                }}
              >
                {/* Use MapComponent instead of MyMap */}
                <MapComponent
                  addNodeCallback={addNodeCallback}
                  registerSearchMapCallback={registerSearchMapCallback}
                  tripLocations={tripLocations}
                  onAddMarker={handleAddMarker}
                  setClearSelectedPlaceCallback={setClearSelectedPlace}
                  editingEvent={editingEvent}
                  setEditingEvent={setEditingEvent}
                  selectedDayIdx={selectedDayIdx}
                  selectedEventIdx={selectedEventIdx}
                  trip={trip}
                  setSelectedDayIdx={setSelectedDayIdx}
                  setSelectedEventIdx={setSelectedEventIdx}
                  selectedEventID={selectedEventID}
                  setSelectedEventID={setSelectedEventID}
                  isMultiSelectMode={isMultiSelectMode}
                  selectedEvents={selectedEvents}
                />
              </div>
              {/* Vertical splitter */}
              <div
                ref={splitterRef}
                className="vertical-splitter"
                style={{
                  height: 8,
                  background: '#e3eafc',
                  cursor: 'row-resize',
                  width: '100%',
                  zIndex: 2,
                  userSelect: 'none'
                }}
                onMouseDown={handleSplitterMouseDown}
              />
              {/* Description viewer */}
              <div
                className="right-pane-desc"
                style={{
                  flex: `1 1 0%`,
                  minHeight: 60,
                  maxHeight: '30%',
                  overflow: 'auto',
                  borderTop: '1.5px solid #b6c3d1',
                  background: '#fafdff'
                }}
              >
                <EventLongDescViewer html={selectedLongDesc} />
              </div>
            </div>
          </div>
        )}
      </div>
    </locationContext.Provider>
  )
};

export default Layout;